import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';

import { Observable, Subject, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { FormGroup } from '@angular/forms';
import { LotList } from './shared/entities/LotList';
import { IParkerImportErrors, IReportInvoiceAging, ICredentialTypeResponse } from './shared/interfaces';
import { IParcsGetLotConfigurationResponse } from './shared/code/interfaces/parcs-interfaces';
import { ParcsType, ParcsTypeHelper } from './shared/code/parcs-type.helper';
import { ParcsDataService } from './core/services/data/parcs-data.service';
import { DataService } from './core/services/data/data.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class AppGeneralStateService {
  public isGbGenerationInProgress: boolean = true;
  public isProcessing: boolean = false;
  public userId: string;
  public isUserAdmin: boolean = false;
  public userName: string;
  public isSuperUser: boolean = false;
  public isEnvironmentDev: boolean = false;
  public importValidationErrors: IParkerImportErrors[] = [];
  public isAuthenticated: boolean = false;
  public isPaymentRefundOverrideActive: boolean = false;
  public hasLinkedParcsAccount: boolean | undefined = undefined;
  public currentLotHasParcs: boolean = false; // When a lot is selected, it checks if a PARCS is linked to it
  public parcsDisplayName: string = '';

  private _currentLotNumber: string = '';
  public get currentLotNumber(): string {
    return this._currentLotNumber;
  }
  public set currentLotNumber(value: string) {
    const hasChanged = this._currentLotNumber !== value;
    this._currentLotNumber = value;

    // Whenever the current lot number changes, refresh the credential type list for that lot.
    if (hasChanged && value) {
      this.fetchCredentialTypeListForLotNumber(value);
    }
  }
  public currentLotName: string = 'No Lot Selected';
  public UsersLotList: LotList[] = [];
  public currentUserEmailAddress: string | undefined = undefined;
  public credentialTypeListForCurrentLotNumber: ICredentialTypeResponse[] = [];

  devLinkId: string = '';
  currentLinkId: string = '';
  devUserId: string = '2F8E121C-883E-4C4C-8BFF-2AE2CA1E6A3B'; //Ken Moss
  validatedUserId: string = '';
  queryCriteriaForm: FormGroup;
  public navMenuDisplay: boolean = true;
  public navDisplayStyle: number = 1;
  public toggleNavMenuDisplay(): void {
    this.navMenuDisplay = !this.navMenuDisplay;
  }

  //Parker Vars
  updateFinishedBroadcast = new Subject();

  //Parking Agreement Vars
  includeLotSpecificContractVerbiage: boolean = false;
  includeSpecialInstructionsLink: boolean = false;
  defaultSendParkingAgreement: boolean = false;
  defaultCollectAddressVehicleInformation: boolean = false;

  // PARCS Configuration Vars
  public currentLotParcsConfiguration: IParcsGetLotConfigurationResponse = {} as IParcsGetLotConfigurationResponse;

  /// Derived from currentLotParcsConfiguration rather than cached, so it stays correct
  /// when other components reset the configuration object directly.
  public get currentLotParcsType(): ParcsType {
    return ParcsTypeHelper.fromLotConfiguration(this.currentLotParcsConfiguration);
  }
  public get isParcsAmanoOne(): boolean {
    return this.currentLotParcsType === ParcsType.AmanoOne;
  }
  public get isParcsFlash(): boolean {
    return this.currentLotParcsType === ParcsType.Flash;
  }

  public canChangeLot: boolean = true;

  //Services
  private _parcsDataService = inject(ParcsDataService);
  private _dataService = inject(DataService);
  private _destroyRefService = inject(DestroyRef);

  //Signals
  public lotNumberSignal = signal('101');
  public lotNumberNameSignal = signal('');

  //Subjects
  public lotNumberSubject = new Subject<string>();
  public lotNumberNameSubject = new Subject<string>();
  public quickSearchReset = new Subject<boolean>();
  public invoiceAgingDataSubject = new Subject<IReportInvoiceAging[]>();
  public resetDateComponent = new Subject<null>();
  public parkerProfileGridRefresh = new Subject<null>();

  public refreshParkerProfileGrid(): void {
    this.parkerProfileGridRefresh.next(null);
  }

  public resetDateSelection(): void {
    this.resetDateComponent.next(null);
  }

  public lotChangeMessage(): void {
    this.quickSearchReset.next(true);
  }

  public PublishInvoiceAgingData(invoiceData: IReportInvoiceAging[]): void {
    this.invoiceAgingDataSubject.next(invoiceData);
  }

  public PublishLotNumberName(lotNumber: string, lotName: string): void {
    this.lotNumberSubject.next(lotNumber);
    this.lotNumberNameSubject.next(`${lotNumber} -  ${lotName}`);
  }

  public getCurrentLotNumberNameDisplay(): string {
    let displayLotNumberName: string = '';

    if (this.currentLotNumber == '') {
      displayLotNumberName = 'No Lot Selected';
    } else {
      displayLotNumberName = this.currentLotNumber + ' - ' + this.currentLotName;
    }

    return displayLotNumberName;
  }

  setCurrentLinkId(linkId: string): void {
    this.currentLinkId = linkId;
  }
  getCurrentLinkId(): string {
    return this.currentLinkId;
  }
  getDevLinkId(): string {
    return this.devLinkId;
  }
  getDevUserId(): string {
    return this.devUserId;
  }
  setValidatedUserId(userId: string): void {
    this.validatedUserId = userId;
  }
  getValidatedUserId(): string {
    return this.validatedUserId;
  }
  getQuickSearchRoutingId(): string {
    return '1';
  }
  getProfileRoutingId(): string {
    return '2';
  }
  getUtilityRoutingId(): string {
    return '3';
  }
  getAdminRoutingId(): string {
    return '4';
  }
  getCompanyRoutingId(): string {
    return '5';
  }
  getHomeRoutingId(): string {
    return '6';
  }

  /// Fetches the credential type list for the given lot number.
  /// On success, sets the credentialTypeListForCurrentLotNumber variable to the returned list.
  public fetchCredentialTypeListForLotNumber(lotNumber: string): void {
    this._dataService
      .getCredentialTypeListForAGivenLotNumber(lotNumber)
      .pipe(takeUntilDestroyed(this._destroyRefService))
      .subscribe({
        next: (res) => {
          this.credentialTypeListForCurrentLotNumber = res;
          console.log('Credential Type List for Lot Number:', lotNumber, this.credentialTypeListForCurrentLotNumber);
        },
        error: (err: any) => {
          this.credentialTypeListForCurrentLotNumber = [];
        },
        complete: () => {},
      });
  }

  /// Determine if this lot has a parcs
  /// Also sets the currentLotParcsConfiguration and currentLotHasParcs variables
  /// The currentLotHasParcs variable can be used throughout the app to determine if parcs related features should be enabled
  public getParcsLotConfigurationData(lotNumber: string) {
    this._parcsDataService
      .getParcsLotConfigurationByLotNumber(lotNumber)
      .pipe(takeUntilDestroyed(this._destroyRefService))
      .subscribe({
        next: (res) => {
          this.currentLotParcsConfiguration = res;

          if (this.currentLotParcsConfiguration.lotNumber == lotNumber) {
            this.currentLotHasParcs = true;
          } else {
            // Clear it so the derived parcs type flags don't report the previously selected lot's vendor.
            this.currentLotParcsConfiguration = {} as IParcsGetLotConfigurationResponse;
            this.currentLotHasParcs = false;
          }
        },
        error: (err: any) => {
          this.currentLotParcsConfiguration = {} as IParcsGetLotConfigurationResponse;
          this.currentLotHasParcs = false; // no configuration found,
        },
        complete: () => {
          console.log(
            'Parcs lot configuration status',
            this.currentLotHasParcs,
            this.currentLotParcsType,
            this.currentLotParcsConfiguration,
          );
          if(this.currentLotHasParcs) {
            this.parcsDisplayName = this.currentLotParcsConfiguration.parcsDisplayName;
          }
        },
      });
  }
}
